import pLimit from 'p-limit';
import { delay } from '@web-config-app/core';
import PQueue from 'p-queue';
import { UnknownEnvironmentError } from '../../errors/unknown-environment-error';

export interface BatchAsyncResult<TResult> {
  /** Successfully resolved results (undefined values are filtered out) */
  results: Awaited<TResult>[];
  /** Number of operations that failed after all retries */
  failedCount: number;
  /** Errors from failed operations (after all retries) */
  errors: unknown[];
}

export interface BatchAsyncRateLimitOptions {
  /**
   * The length of the interval window (ms).
   * When set, at most `intervalCap` tasks will be STARTED within each interval window.
   */
  intervalMs: number;
  /**
   * Maximum number of tasks that can start within each `intervalMs` window.
   * (This is a global rate limit across all concurrency slots.)
   */
  intervalCap: number;
  /**
   * If true, pending concurrency from the previous interval counts against the next interval.
   * This helps keep a strict upper bound on request starts under long-running tasks.
   */
  carryoverConcurrencyCount?: boolean;
}

interface IndexedItem<TItem> {
  item: TItem;
  originalIndex: number;
}

interface FailedItem<TItem> extends IndexedItem<TItem> {
  error: unknown;
}

const notifyTerminalFailures = async <TItem>(
  failedItems: Array<FailedItem<TItem>>,
  onItemFailed?: (params: {
    item: TItem;
    error: unknown;
  }) => Promise<void> | void,
) => {
  await failedItems.reduce<Promise<void>>(async (chain, failedItem) => {
    await chain;
    await onItemFailed?.({ item: failedItem.item, error: failedItem.error });
  }, Promise.resolve());
};

const notifyItemsRetrying = async <TItem>(
  retryingItems: Array<IndexedItem<TItem>>,
  onItemRetrying?: (params: { item: TItem }) => Promise<void> | void,
) => {
  await retryingItems.reduce<Promise<void>>(async (chain, retryingItem) => {
    await chain;
    await onItemRetrying?.({ item: retryingItem.item });
  }, Promise.resolve());
};

const filterUndefined = <T>(
  value: T | undefined,
): value is Exclude<T, undefined> => value !== undefined;

/** HTTP 429 Too Many Requests - Rate limit exceeded */
const HTTP_RATE_LIMIT = 429;
/** HTTP 5xx range start - Server errors */
const HTTP_SERVER_ERROR_MIN = 500;
/** HTTP 5xx range end (exclusive) */
const HTTP_SERVER_ERROR_MAX = 600;

/**
 * Determines if an error is retryable (temporary and worthy of retrying) based on HTTP status code.
 * Retryable errors: 429 (rate limit), 5xx (server errors), network errors (no numeric status)
 * Non-retryable errors: 4xx client errors (except 429) — these are permanent failures
 */
export const isRetryableError = (error: unknown): boolean => {
  // A misconfigured environment is not a transient failure: retrying re-resolves the same
  // bad key. Without this it falls into the no-status branch below and burns the full retry
  // budget, with backoff, once per entity in a bulk copy.
  if (error instanceof UnknownEnvironmentError) return false;

  const status = (error as { status?: number })?.status;
  // No numeric status typically means a network error — retry these
  if (typeof status !== 'number') return true;

  const isRateLimit = status === HTTP_RATE_LIMIT;
  const isServerError =
    status >= HTTP_SERVER_ERROR_MIN && status < HTTP_SERVER_ERROR_MAX;
  return isRateLimit || isServerError;
};

/**
 * Processes an array of items concurrently with a configurable concurrency limit.
 * Uses p-limit for efficient sliding-window concurrency control - as soon as one
 * operation completes, the next one starts (rather than waiting for batches).
 *
 * @param items - Array of items to process
 * @param operation - Async function to execute on each item
 * @param options - Configuration options
 * @param options.concurrency - Maximum number of concurrent operations (default: 10)
 * @param options.callInterval - Time in milliseconds to wait after each operation completes (default: 0)
 * @param options.continueOnError - If true, continues processing remaining items when one fails (default: false)
 * @param options.maxRetries - Maximum number of retry attempts for failed items (default: 0). Only applies when continueOnError is true.
 * @param options.retryDelayMs - Time in milliseconds to wait before each retry attempt (default: 0)
 * @param options.shouldRetry - Function to determine if a failed item should be retried based on its error. Defaults to retrying all errors if not provided and maxRetries is greater than 0. If provided, only retry errors that pass the function check.
 * @param options.rateLimit - Optional global rate limit on task STARTS (powered by p-queue interval/intervalCap)
 *
 * Note:
 * - `p-limit` only provides concurrency control.
 * - `callInterval` is a per-operation cooldown (keeps a concurrency "slot" busy).
 * - `rateLimit` is a true "max N starts per interval" limiter (global across slots).
 * - When `maxRetries` > 0, failed items are collected and retried at the end of each batch.
 * - Use `shouldRetry` to only retry retryable errors (e.g., 429, 5xx) and skip permanent failures.
 *
 * @returns BatchAsyncResult with results array and failedCount (after all retries)
 *
 * @example
 * ```typescript
 * const userIds = ['user-1', 'user-2', 'user-3', ...];
 *
 * const { results } = await batchAsyncOperations({
 *   items: userIds,
 *   operation: (userId) => fetchUser(userId),
 *   concurrency: 5,
 *   callInterval: 100, // wait 100ms after each request
 *   continueOnError: true,
 *   maxRetries: 3, // retry failed items up to 3 times
 *   retryDelayMs: 3000, // wait 3 seconds before each retry
 *   shouldRetry: isRetryableError, // only retry 429/5xx errors
 * });
 * ```
 */
export async function batchAsyncOperations<TItem, TResult>({
  items,
  operation,
  concurrency = 10,
  callInterval = 0,
  rateLimit,
  continueOnError = false,
  maxRetries = 0,
  retryDelayMs = 0,
  shouldRetry = () => true,
  onItemFailed,
  onItemRetrying,
  abortSignal,
}: {
  items: TItem[];
  operation: (item: TItem) => Promise<TResult>;
  concurrency?: number;
  callInterval?: number;
  rateLimit?: BatchAsyncRateLimitOptions;
  continueOnError?: boolean;
  maxRetries?: number;
  retryDelayMs?: number;
  shouldRetry?: (error: unknown) => boolean;
  onItemFailed?: (params: {
    item: TItem;
    error: unknown;
  }) => Promise<void> | void;
  onItemRetrying?: (params: { item: TItem }) => Promise<void> | void;
  abortSignal?: AbortSignal;
}): Promise<BatchAsyncResult<TResult>> {
  // Create a single queue so rate limiting applies across initial run + retries.
  const rateLimitQueue =
    rateLimit && rateLimit.intervalMs > 0 && rateLimit.intervalCap > 0
      ? new PQueue({
          concurrency,
          interval: rateLimit.intervalMs,
          intervalCap: rateLimit.intervalCap,
          carryoverConcurrencyCount:
            rateLimit.carryoverConcurrencyCount ?? true,
        })
      : null;

  const concurrencyLimiter = pLimit(concurrency);

  const enqueueWithRateLimit = <T>(fn: () => Promise<T>): Promise<T> =>
    rateLimitQueue
      ? (rateLimitQueue.add(fn) as Promise<T>)
      : concurrencyLimiter(fn);

  if (abortSignal && rateLimitQueue) {
    abortSignal.addEventListener(
      'abort',
      () => {
        rateLimitQueue.clear();
      },
      { once: true },
    );
  }

  const throwIfAborted = () => {
    if (abortSignal?.aborted) {
      throw new DOMException('Operation was cancelled', 'AbortError');
    }
  };

  const executeWithInterval = async (item: TItem): Promise<TResult> => {
    throwIfAborted();
    try {
      return await operation(item);
    } finally {
      if (callInterval > 0) {
        await delay(callInterval);
      }
    }
  };

  const executeOperationsInParallel = async (
    batchItems: Array<IndexedItem<TItem>>,
  ): Promise<{
    successfulResults: Array<{
      result: Awaited<TResult>;
      originalIndex: number;
    }>;
    failedItems: Array<FailedItem<TItem>>;
  }> => {
    const scheduledOperations = batchItems.map(({ item }) =>
      enqueueWithRateLimit(() => executeWithInterval(item)),
    );

    const settled = await Promise.allSettled(scheduledOperations);

    const successfulResults: Array<{
      result: Awaited<TResult>;
      originalIndex: number;
    }> = [];
    const failedItems: Array<FailedItem<TItem>> = [];

    settled.forEach((outcome, idx) => {
      const { item, originalIndex } = batchItems[idx];
      if (outcome.status === 'fulfilled') {
        successfulResults.push({ result: outcome.value, originalIndex });
      } else {
        failedItems.push({
          item,
          originalIndex,
          error: outcome.reason,
        });
      }
    });

    return { successfulResults, failedItems };
  };

  throwIfAborted();

  // For non-continueOnError mode, use the simpler Promise.all approach
  if (!continueOnError) {
    const scheduledOperations = items.map((item) =>
      enqueueWithRateLimit(() => executeWithInterval(item)),
    );

    // Promise.all rejects on first error, so reaching the return means all succeeded
    const results = await Promise.all(scheduledOperations);
    return {
      results: results.filter(filterUndefined),
      failedCount: 0,
      errors: [],
    };
  }

  // Initial batch with original indices (so we can preserve order later)
  const initialBatchResult = await executeOperationsInParallel(
    items.map((item, originalIndex) => ({ item, originalIndex })),
  );
  let { successfulResults } = initialBatchResult;
  const { failedItems } = initialBatchResult;

  // Separate retryable failures (can retry) from permanent failures (cannot retry)
  let pendingRetries = failedItems.filter((failedItem) =>
    shouldRetry(failedItem.error),
  );
  const permanentFailures = failedItems.filter(
    (failedItem) => !shouldRetry(failedItem.error),
  );
  await notifyTerminalFailures(permanentFailures, onItemFailed);

  // Retry retryable failures up to maxRetries times
  let retryAttempt = 0;
  while (
    pendingRetries.length > 0 &&
    retryAttempt < maxRetries &&
    !abortSignal?.aborted
  ) {
    retryAttempt += 1;

    // eslint-disable-next-line no-await-in-loop
    await notifyItemsRetrying(pendingRetries, onItemRetrying);

    // Wait before retry attempt
    if (retryDelayMs > 0) {
      // eslint-disable-next-line no-await-in-loop
      await delay(retryDelayMs);
    }

    // Retry only the subset that failed in the previous pass
    // eslint-disable-next-line no-await-in-loop
    const retryResult = await executeOperationsInParallel(pendingRetries);

    successfulResults = successfulResults.concat(retryResult.successfulResults);

    // Check which failures from this retry are still retryable (can retry again)
    pendingRetries = retryResult.failedItems.filter((failedItem) =>
      shouldRetry(failedItem.error),
    );
    // Failures that are no longer retryable get moved to permanent failures
    const newPermanentFailures = retryResult.failedItems.filter(
      (failedItem) => !shouldRetry(failedItem.error),
    );
    // eslint-disable-next-line no-await-in-loop
    await notifyTerminalFailures(newPermanentFailures, onItemFailed);
    permanentFailures.push(...newPermanentFailures);
  }

  if (!abortSignal?.aborted) {
    await notifyTerminalFailures(pendingRetries, onItemFailed);
  }

  // Combine all remaining failures (exhausted retries + permanent failures)
  const allFailedItems = [...pendingRetries, ...permanentFailures];

  // Sort results by original index to maintain order
  successfulResults.sort(
    (leftResult, rightResult) =>
      leftResult.originalIndex - rightResult.originalIndex,
  );
  const results = successfulResults
    .map((resultEntry) => resultEntry.result)
    .filter(filterUndefined);

  // Collect errors from all failed items
  const errors = allFailedItems.map((failedItem) => failedItem.error);

  return { results, failedCount: allFailedItems.length, errors };
}
